import { READ_TIME_LIMIT_MS } from '@mxa-argos/modules/forja/constants/forja.constants';
import { ForjaError } from '@mxa-argos/modules/forja/lib/forja-error';
import { Adb, AdbDaemonTransport } from '@yume-chan/adb';
import AdbWebCredentialStore from '@yume-chan/adb-credential-web';
import type { AdbDaemonWebUsbDevice } from '@yume-chan/adb-daemon-webusb';

const credentialStore = new AdbWebCredentialStore('Forja');

export async function connectDevice(device: AdbDaemonWebUsbDevice): Promise<Adb> {
  const connection = await openConnection(device);

  // authenticate() only settles once the USB debugging prompt is answered, which on a full bench
  // takes as long as the operator needs. Timing it out here would strand the device: nothing
  // re-registers it short of a replug.
  const transport = await AdbDaemonTransport.authenticate({
    serial: device.serial,
    connection,
    credentialStore,
    readTimeLimit: READ_TIME_LIMIT_MS,
  });

  return new Adb(transport);
}

// A hot reload drops the Adb reference but not the USB claim, so retry once after releasing it.
async function openConnection(device: AdbDaemonWebUsbDevice) {
  try {
    return await device.connect();
  } catch (error) {
    if (!isInterfaceBusy(error)) {
      throw error;
    }

    await device.raw.close().catch(() => undefined);

    return device.connect().catch(() => {
      throw new ForjaError('DEVICE_BUSY', String(error));
    });
  }
}

function isInterfaceBusy(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();

  return (
    message.includes('claim') || message.includes('access denied') || message.includes('in use')
  );
}
